/**
 * Piattaforma (V54-V56): forma dei dati e aiuti puri.
 *
 * Le regole vere le applica il server; qui si ricavano solo le etichette e
 * le opzioni da mostrare, per non proporre una nomina che il server
 * rifiuterebbe comunque.
 */

import type { AccountSocieta, RuoloAccount } from '../amministrazione/ruoli.js';
import { formatoIstante } from '../comuni/date.js';

export interface Rapporto {
  usato: number;
  massimo: number;
}

export interface Limiti {
  squadreStagione: Rapporto;
  giocatori: Rapporto;
  account: Rapporto;
  /** Domande all'assistente nel mese corrente. */
  assistente: Rapporto;
}

export interface Invito {
  scadeIl: string;
  scaduto: boolean;
}

export interface AdminSocieta {
  id: string;
  nome: string;
  cognome: string;
  email: string;
  invito: Invito | null;
}

export interface SocietaInElenco {
  id: string;
  nome: string;
  slug: string;
  attiva: boolean;
  creataIl: string;
  limiti: Limiti;
  admin: AdminSocieta | null;
}

export interface SchedaSocieta extends SocietaInElenco {
  account: AccountSocieta[];
}

export interface AccountTrovato {
  id: string;
  email: string;
  nome: string;
  cognome: string;
  superAdmin: boolean;
  ultimoAccesso: string | null;
  societa: {
    id: string;
    nome: string;
    attiva: boolean;
    appartenenzaAttiva: boolean;
    invito: Invito | null;
    ruoli: {
      ruolo: RuoloAccount;
      squadra: string | null;
      categoria: string | null;
      stagione: string | null;
    }[];
  } | null;
}

export interface RicercaAccount {
  account: AccountTrovato[];
  altri: boolean;
}

/** "12 / 30". */
export const rapporto = (r: Rapporto): string => `${r.usato} / ${r.massimo}`;

export const pieno = (r: Rapporto): boolean => r.usato >= r.massimo;

/**
 * Chi puo diventare Admin Squadra: account attivi della societa che hanno
 * gia scelto la password, tranne l'Admin attuale.
 */
export const candidatiAdmin = (
  account: readonly AccountSocieta[],
  adminId: string | null,
): AccountSocieta[] => account.filter((a) => a.attivo && !a.invito && a.id !== adminId);

/** Lo stato di un account trovato da V56, dal piu bloccante. */
export function statoAccount(a: AccountTrovato): {
  etichetta: string;
  tono: 'normale' | 'attenzione' | 'spento';
} {
  if (a.superAdmin && !a.societa)
    return { etichetta: 'Gestore della piattaforma', tono: 'normale' };
  if (!a.societa) return { etichetta: 'Senza società', tono: 'spento' };
  if (!a.societa.attiva) return { etichetta: 'Società disattivata', tono: 'spento' };
  if (a.societa.invito) {
    return a.societa.invito.scaduto
      ? { etichetta: 'Invito scaduto', tono: 'attenzione' }
      : { etichetta: 'Invitato', tono: 'normale' };
  }
  if (!a.societa.appartenenzaAttiva) return { etichetta: 'Disattivato', tono: 'spento' };
  return { etichetta: 'Attivo', tono: 'normale' };
}

/** "17/09/2026", dal giorno italiano di un istante. */
const formatoGiornoIstante = formatoIstante({ day: '2-digit', month: '2-digit', year: 'numeric' });

export const giorno = (istante: string): string => formatoGiornoIstante.format(new Date(istante));

/** Lo slug fisso della società creata da «Crea società demo». */
export const SLUG_DEMO = 'demo';

export const eDemo = (s: { slug: string }): boolean => s.slug === SLUG_DEMO;

export interface AccessoDemo {
  ruolo: 'admin_squadra' | 'mister' | 'dirigente';
  squadra?: string;
  email: string;
  password: string;
}

export interface EsitoSocietaDemo {
  id: string;
  nome: string;
  slug: string;
  accessi: AccessoDemo[];
}

export function etichettaAccessoDemo(a: AccessoDemo): string {
  if (a.ruolo === 'admin_squadra') return 'Admin Squadra';
  const ruolo = a.ruolo === 'mister' ? 'Mister' : 'Dirigente';
  return a.squadra ? `${ruolo} ${a.squadra}` : ruolo;
}
